/**
 * React Query hooks for Settings API (COMP-008).
 * IP-009: GET /api/settings, PUT /api/settings
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/providers/api-provider";
import type { PortalSettings } from "@/types";

export function useSettings() {
  const api = useApiClient();

  return useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<PortalSettings>("/api/settings"),
    staleTime: 300_000, // 5 minutes
  });
}

export function useUpdateSettings() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: Partial<PortalSettings>) =>
      api.put<PortalSettings>("/api/settings", settings),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}

// ── Notification channel settings (HaloPSA ticket type + priorities) ──

export interface NotificationChannelSettings {
  tickettypeId: number | null;
  priorityDefault: number;
  priorityCritical: number;
  emailSenderAddress: string;
  emailSenderUserId: string | null;
}

export function useNotificationSettings() {
  const api = useApiClient();

  return useQuery({
    queryKey: ["notification-settings"],
    queryFn: () =>
      api.get<NotificationChannelSettings>(
        "/api/admin/settings/notification-settings",
      ),
    staleTime: 300_000,
  });
}

export function useUpdateHaloPsaTicketType() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tickettypeId: number) =>
      api.put<{ updated: boolean }>(
        "/api/admin/settings/halopsa-ticket-type",
        { tickettypeId },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["notification-settings"],
      });
    },
  });
}

export function useUpdateHaloPsaPriorities() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (priorities: { default: number; critical: number }) =>
      api.put<{ updated: boolean }>(
        "/api/admin/settings/halopsa-priority-mapping",
        priorities,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["notification-settings"],
      });
    },
  });
}
